import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, Monitor, Smartphone, Globe, Eye } from 'lucide-react';

const UserMetricsCard = ({ data, loading = false }) => {
    if (loading) {
        return (
            <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base sm:text-lg font-medium text-foreground">User Metrics</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="animate-pulse space-y-4">
                        <div className="h-4 bg-muted rounded"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                        <div className="h-4 bg-muted rounded w-1/2"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const { anonymousPercentage, deviceBreakdown } = data;

    return (
        <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-primary/[0.01] to-transparent pointer-events-none" />
            <CardHeader className="pb-2">
                <div className="space-y-1">
                    <CardTitle className="text-base sm:text-lg font-medium text-foreground flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary/60" />
                        User Insights
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-muted-foreground">Anonymous users and device breakdown</p>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="space-y-4">
                    {/* Anonymous Users */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Eye className="h-3 w-3 text-muted-foreground" />
                                <span className="text-sm font-medium">Anonymous Users</span>
                            </div>
                            <span className="text-sm font-bold text-blue-600">{anonymousPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                            <motion.div
                                className="bg-blue-500 h-2 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${anonymousPercentage}%` }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                            />
                        </div>
                    </div>

                    {/* Top Browsers */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Globe className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-medium">Top Browsers</span>
                        </div>
                        <div className="space-y-1">
                            {deviceBreakdown.browsers.slice(0, 3).map((browser, index) => (
                                <div key={index} className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">{browser.browser}</span>
                                    <span className="font-medium">{browser.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Top Operating Systems */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Monitor className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-medium">Top OS</span>
                        </div>
                        <div className="space-y-1">
                            {deviceBreakdown.operatingSystems.slice(0, 3).map((os, index) => (
                                <div key={index} className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">{os.os}</span>
                                    <span className="font-medium">{os.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Top Devices */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Smartphone className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-medium">Top Devices</span>
                        </div>
                        <div className="space-y-1">
                            {deviceBreakdown.devices.slice(0, 3).map((device, index) => (
                                <div key={index} className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">{device.device}</span>
                                    <span className="font-medium">{device.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default UserMetricsCard; 